import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  type BlogPostAdminListItem,
  type PaginatedResponse,
  LOCALES,
  type Locale,
  SlugSchema,
  hasNextPage,
} from '@bailafm/shared';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { PaginationControls } from '@/components/PaginationControls';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { BlogDeleteDialog } from '@/components/blog/BlogDeleteDialog';
import { ApiClientError, blogApi } from '@/lib/api';
import { slugify, slugifyInput } from '@/lib/crop-image';
import { cn } from '@/lib/utils';

const PAGE_SIZE = 20;

export default function BlogPage() {
  const navigate = useNavigate();
  const [pageData, setPageData] = useState<PaginatedResponse<BlogPostAdminListItem> | null>(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [toDelete, setToDelete] = useState<BlogPostAdminListItem | null>(null);

  // Descarta respuestas fuera de orden: si dos llamadas a refresh() están
  // en vuelo (p. ej. doble clic en paginación), solo aplica el resultado
  // de la más reciente.
  const latestRequestRef = useRef(0);

  const refresh = useCallback(async (p: number) => {
    const requestId = ++latestRequestRef.current;
    setLoading(true);
    setLoadError(null);
    try {
      const data = await blogApi.list(p, PAGE_SIZE);
      if (requestId !== latestRequestRef.current) return;
      setPageData(data);
    } catch (err) {
      if (requestId !== latestRequestRef.current) return;
      setLoadError(
        err instanceof ApiClientError ? err.message : 'No se pudieron cargar las entradas.',
      );
    } finally {
      if (requestId === latestRequestRef.current) setLoading(false);
    }
  }, [setPageData, setLoading, setLoadError]);

  useEffect(() => {
    void refresh(page);
  }, [page, refresh]);

  const totalPages = pageData ? Math.ceil(pageData.total / PAGE_SIZE) : 0;

  async function handleDelete(post: BlogPostAdminListItem) {
    await blogApi.delete(post.id, post._etag ?? '');
    const wasLastItemOnPage = pageData?.items.length === 1 && pageData.page > 1;
    setPageData((prev) => {
      if (!prev) return prev;
      const items = prev.items.filter((p) => p.id !== post.id);
      const total = prev.total - 1;
      return { ...prev, items, total, hasNextPage: hasNextPage(prev.page, prev.pageSize, total) };
    });
    if (wasLastItemOnPage) {
      setPage((p) => p - 1);
    }
  }

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <h1 className="text-3xl font-semibold tracking-tight">Blog</h1>
          <p className="text-muted-foreground">
            Noticias del blog. Cada entrada tiene título y subtítulo trilingües, imágenes y un
            cuerpo por bloques.
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="size-4" aria-hidden /> Nueva entrada
        </Button>
      </header>

      {loadError && (
        <p className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {loadError}
        </p>
      )}

      {loading && (
        <p className="rounded-lg border bg-card p-4 text-sm text-muted-foreground">Cargando…</p>
      )}

      {!loading && pageData && pageData.items.length === 0 && !loadError && (
        <p className="rounded-lg border bg-card p-6 text-center text-sm text-muted-foreground">
          Todavía no hay entradas. Crea la primera para empezar.
        </p>
      )}

      {!loading && pageData && pageData.items.length > 0 && (
        <>
          <ul className="space-y-2">
            {pageData.items.map((post) => (
              <li key={post.id}>
                <BlogRow
                  post={post}
                  onEdit={() => navigate(`/admin/blog/${post.id}`)}
                  onDelete={() => setToDelete(post)}
                />
              </li>
            ))}
          </ul>

          {totalPages > 1 && (
            <PaginationControls page={page} totalPages={totalPages} onPageChange={setPage} />
          )}
        </>
      )}

      <BlogCreateDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={(id) => navigate(`/admin/blog/${id}`)}
      />

      <BlogDeleteDialog post={toDelete} onClose={() => setToDelete(null)} onConfirm={handleDelete} />
    </div>
  );
}

interface BlogRowProps {
  post: BlogPostAdminListItem;
  onEdit(): void;
  onDelete(): void;
}

function BlogRow({ post, onEdit, onDelete }: BlogRowProps) {
  const preview = post.title.es ?? post.title.ca ?? post.title.en ?? '(sin traducción)';
  return (
    <article className="flex flex-wrap items-center gap-3 rounded-lg border bg-card p-3">
      {post.thumbnail ? (
        <img src={post.thumbnail.url} alt="" className="h-12 w-20 shrink-0 rounded object-cover" />
      ) : (
        <div aria-hidden className="h-12 w-20 shrink-0 rounded bg-muted" />
      )}
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="truncate text-sm font-medium">{preview}</span>
          <StatusBadge status={post.status} />
          <LocalesBadges title={post.title} />
        </div>
        <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
          <span>/{post.slug}</span>
          <span aria-hidden>·</span>
          <span>{post.publishedAt ? `Publicada ${formatDate(post.publishedAt)}` : 'Sin publicar'}</span>
        </div>
      </div>
      <div className="flex items-center gap-1">
        <Button type="button" variant="ghost" size="icon" aria-label="Editar" onClick={onEdit}>
          <Pencil className="size-4" aria-hidden />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label="Eliminar"
          onClick={onDelete}
          className="text-destructive hover:text-destructive"
        >
          <Trash2 className="size-4" aria-hidden />
        </Button>
      </div>
    </article>
  );
}

function StatusBadge({ status }: { status: BlogPostAdminListItem['status'] }) {
  const published = status === 'published';
  return (
    <span
      className={cn(
        'rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
        published ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700',
      )}
    >
      {published ? 'Publicada' : 'Borrador'}
    </span>
  );
}

function LocalesBadges({ title }: { title: BlogPostAdminListItem['title'] }) {
  return (
    <span className="flex items-center gap-1">
      {LOCALES.map((l) => {
        const present = typeof title[l as Locale] === 'string';
        return (
          <span
            key={l}
            className={cn(
              'rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
              present ? 'bg-foreground text-background' : 'bg-muted text-muted-foreground/60',
            )}
            aria-label={`${l} ${present ? 'presente' : 'ausente'}`}
          >
            {l}
          </span>
        );
      })}
    </span>
  );
}

function formatDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' });
}

interface BlogCreateDialogProps {
  open: boolean;
  onOpenChange(open: boolean): void;
  onCreated(id: string): void;
}

function BlogCreateDialog({ open, onOpenChange, onCreated }: BlogCreateDialogProps) {
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTitle('');
    setSlug('');
    setSlugTouched(false);
    setError(null);
  }, [open]);

  useEffect(() => {
    if (slugTouched) return;
    setSlug(slugify(title));
  }, [title, slugTouched]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!title.trim()) {
      setError('El título en español es obligatorio.');
      return;
    }
    // Normaliza el guion final residual (p. ej. "bad-bunny-") antes de validar/enviar.
    const finalSlug = slugify(slug);
    if (!SlugSchema.safeParse(finalSlug).success) {
      setError('El slug debe ser kebab-case: minúsculas, números y guiones.');
      return;
    }
    setSubmitting(true);
    try {
      const created = await blogApi.create({
        slug: finalSlug,
        type: 'news',
        title: { es: title.trim() },
        blocks: [],
      });
      toast.success('Entrada creada');
      onOpenChange(false);
      onCreated(created.id);
    } catch (err) {
      const message =
        err instanceof ApiClientError && err.code === 'blog_post_slug_taken'
          ? 'Ya existe una entrada con ese slug. Elige otro.'
          : err instanceof ApiClientError
            ? err.message
            : 'No se pudo crear la entrada.';
      setError(message);
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Nueva entrada</DialogTitle>
          <DialogDescription>
            El título en español es obligatorio; el slug se genera de él y no se puede cambiar
            después. Tras crearla podrás editar el resto.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="new-title">Título (ES)</Label>
            <Input
              id="new-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Bad Bunny en la Super Bowl"
              autoComplete="off"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="new-slug">Slug</Label>
            <Input
              id="new-slug"
              value={slug}
              onChange={(e) => {
                setSlugTouched(true);
                // Saneo tolerante mientras se teclea (conserva el guion
                // en curso); la normalización final va en onBlur.
                setSlug(slugifyInput(e.target.value));
              }}
              onBlur={() => setSlug((s) => slugify(s))}
              placeholder="bad-bunny-super-bowl-2026"
              autoComplete="off"
            />
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="ghost">
                Cancelar
              </Button>
            </DialogClose>
            <Button type="submit" disabled={submitting}>
              {submitting ? 'Creando…' : 'Crear y editar'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
